if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        if (!this.hasAttribute('available')) return;

        this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.fetchAvailability(this.dataset.variantId);
      }

      fetchAvailability(variantId) {
        if (!variantId) return;

        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        fetch(variantSectionUrl)
          .then((response) => response.text())
          .then((text) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((e) => {
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError();
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        if (variant?.available) {
          this.fetchAvailability(variant.id);
        } else {
          this.removeAttribute('available');
          this.innerHTML = '';
        }
      }

      renderError() {
        this.innerHTML = '';
        this.appendChild(this.errorHtml);

        this.querySelector('button').addEventListener('click', this.onClickRefreshList);
      }

      renderPreview(sectionInnerHTML) {
        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer) drawer.remove();
        if (!sectionInnerHTML.querySelector('pickup-availability-preview')) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML;
        this.setAttribute('available', '');

        document.body.appendChild(sectionInnerHTML.querySelector('pickup-availability-drawer'));
        const colorClassesToApply = this.dataset.productPageColorScheme.split(' ');
        colorClassesToApply.forEach((colorClass) => {
          document.querySelector('pickup-availability-drawer').classList.add(colorClass);
        });

        const button = this.querySelector('button');
        if (button)
          button.addEventListener('click', (evt) => {
            document.querySelector('pickup-availability-drawer').show(evt.target);
          });
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          target.id != 'ShowPickupAvailabilityDrawer'
        ) {
          this.hide();
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
    }
  );
}


/* ============================================================
   VF / Pebble & Wick — Cinematic Pickup Availability Enhancements
   Safe layer on top of Dawn pickup-availability.js.
   Keeps store pickup fetching, variant updates, drawer opening and focus trap intact.
   ============================================================ */

(() => {
  const PickupAvailabilityElement = customElements.get('pickup-availability');
  const PickupAvailabilityDrawerElement = customElements.get('pickup-availability-drawer');

  if (!PickupAvailabilityElement || PickupAvailabilityElement.prototype.__vfCinematicPatched) return;

  PickupAvailabilityElement.prototype.__vfCinematicPatched = true;

  const originalFetchAvailability = PickupAvailabilityElement.prototype.fetchAvailability;
  const originalUpdate = PickupAvailabilityElement.prototype.update;
  const originalRenderError = PickupAvailabilityElement.prototype.renderError;
  const originalRenderPreview = PickupAvailabilityElement.prototype.renderPreview;
  const originalOnClickRefreshList = PickupAvailabilityElement.prototype.onClickRefreshList;

  const root = document.documentElement;

  const setPickupState = (pickup, state, detail = {}) => {
    pickup.dataset.vfPickupState = state;

    pickup.classList.toggle('vf-pickup-loading', state === 'loading');
    pickup.classList.toggle('vf-pickup-ready', state === 'ready');
    pickup.classList.toggle('vf-pickup-error', state === 'error');
    pickup.classList.toggle('vf-pickup-unavailable', state === 'unavailable');
    pickup.classList.toggle('vf-pickup-refreshing', state === 'refreshing');

    root.classList.toggle('vf-pickup-is-loading', state === 'loading' || state === 'refreshing');

    pickup.dispatchEvent(
      new CustomEvent(`vf:pickup-availability-${state}`, {
        bubbles: true,
        detail
      })
    );
  };

  const enhancePickup = (pickup) => {
    if (!pickup || pickup.__vfCinematicEnhanced) return;
    pickup.__vfCinematicEnhanced = true;

    pickup.classList.add('vf-pickup-availability-enhanced');

    pickup.querySelector('pickup-availability-preview')?.classList.add('vf-pickup-preview');
    pickup.querySelector('button')?.classList.add('vf-pickup-button');

    setPickupState(pickup, pickup.hasAttribute('available') ? 'ready' : 'unavailable', {
      initial: true,
      variantId: pickup.dataset.variantId
    });
  };

  const enhanceDrawer = (drawer) => {
    if (!drawer || drawer.__vfCinematicEnhanced) return;
    drawer.__vfCinematicEnhanced = true;

    drawer.classList.add('vf-pickup-drawer-enhanced');

    drawer.querySelector('button')?.classList.add('vf-pickup-drawer__close');
    drawer.querySelectorAll('li, .pickup-availability-list__item').forEach((item, index) => {
      item.classList.add('vf-pickup-drawer__item');
      item.style.setProperty('--vf-pickup-item-index', index);
    });
  };

  PickupAvailabilityElement.prototype.fetchAvailability = function vfFetchAvailability(variantId) {
    if (!variantId) return originalFetchAvailability.call(this, variantId);

    setPickupState(this, 'loading', {
      variantId
    });

    return originalFetchAvailability.call(this, variantId);
  };

  PickupAvailabilityElement.prototype.onClickRefreshList = function vfOnClickRefreshList() {
    setPickupState(this, 'refreshing', {
      variantId: this.dataset.variantId
    });

    return originalOnClickRefreshList.call(this);
  };

  PickupAvailabilityElement.prototype.update = function vfUpdate(variant) {
    const result = originalUpdate.call(this, variant);

    if (!variant?.available) {
      setPickupState(this, 'unavailable', {
        variant
      });
    } else {
      this.dataset.vfVariantId = variant.id;
    }

    return result;
  };

  PickupAvailabilityElement.prototype.renderError = function vfRenderError() {
    const result = originalRenderError.call(this);

    this.querySelector('button')?.classList.add('vf-pickup-button', 'vf-pickup-error__button');
    setPickupState(this, 'error', {
      variantId: this.dataset.variantId
    });

    return result;
  };

  PickupAvailabilityElement.prototype.renderPreview = function vfRenderPreview(sectionInnerHTML) {
    const result = originalRenderPreview.call(this, sectionInnerHTML);

    const isAvailable = this.hasAttribute('available');

    enhancePickup(this);

    const preview = this.querySelector('pickup-availability-preview');
    if (preview) {
      preview.classList.add('vf-pickup-preview');
    }

    const button = this.querySelector('button');
    if (button) {
      button.classList.add('vf-pickup-button');
      button.setAttribute('aria-haspopup', 'dialog');
    }

    document.querySelectorAll('pickup-availability-drawer').forEach(enhanceDrawer);

    setPickupState(this, isAvailable ? 'ready' : 'unavailable', {
      variantId: this.dataset.variantId,
      available: isAvailable
    });

    return result;
  };

  if (PickupAvailabilityDrawerElement && !PickupAvailabilityDrawerElement.prototype.__vfCinematicPatched) {
    PickupAvailabilityDrawerElement.prototype.__vfCinematicPatched = true;

    const originalDrawerShow = PickupAvailabilityDrawerElement.prototype.show;
    const originalDrawerHide = PickupAvailabilityDrawerElement.prototype.hide;
    const originalHandleBodyClick = PickupAvailabilityDrawerElement.prototype.handleBodyClick;

    const setDrawerState = (drawer, state, detail = {}) => {
      drawer.dataset.vfPickupDrawerState = state;

      drawer.classList.toggle('vf-pickup-drawer-open', state === 'open');
      drawer.classList.toggle('vf-pickup-drawer-opening', state === 'opening');
      drawer.classList.toggle('vf-pickup-drawer-closing', state === 'closing');

      root.classList.toggle('vf-pickup-drawer-is-open', state === 'open' || state === 'opening');
      document.body?.classList.toggle('vf-pickup-drawer-is-open', state === 'open' || state === 'opening');

      drawer.dispatchEvent(
        new CustomEvent(`vf:pickup-drawer-${state}`, {
          bubbles: true,
          detail
        })
      );
    };

    PickupAvailabilityDrawerElement.prototype.show = function vfDrawerShow(focusElement) {
      enhanceDrawer(this);

      setDrawerState(this, 'opening', {
        focusElement
      });

      const result = originalDrawerShow.call(this, focusElement);

      window.requestAnimationFrame(() => {
        setDrawerState(this, 'open', {
          focusElement
        });
      });

      return result;
    };

    PickupAvailabilityDrawerElement.prototype.hide = function vfDrawerHide() {
      setDrawerState(this, 'closing', {
        focusElement: this.focusElement
      });

      const result = originalDrawerHide.call(this);

      window.setTimeout(() => {
        setDrawerState(this, 'closed', {
          focusElement: this.focusElement
        });
      }, 260);

      return result;
    };

    PickupAvailabilityDrawerElement.prototype.handleBodyClick = function vfHandleBodyClick(event) {
      const result = originalHandleBodyClick.call(this, event);

      if (!this.hasAttribute('open')) {
        this.classList.add('vf-pickup-drawer-click-outside');

        window.setTimeout(() => {
          this.classList.remove('vf-pickup-drawer-click-outside');
        }, 360);
      }

      return result;
    };
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('pickup-availability').forEach(enhancePickup);
    document.querySelectorAll('pickup-availability-drawer').forEach(enhanceDrawer);
  }, { once: true });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('pickup-availability button, pickup-availability-drawer button');
    if (!trigger) return;

    trigger.classList.add('vf-pickup-clicked');

    window.setTimeout(() => {
      trigger.classList.remove('vf-pickup-clicked');
    }, 420);
  });

  const pickupObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;

        if (node.matches?.('pickup-availability')) {
          enhancePickup(node);
        }

        if (node.matches?.('pickup-availability-drawer')) {
          enhanceDrawer(node);
        }

        node.querySelectorAll?.('pickup-availability').forEach(enhancePickup);
        node.querySelectorAll?.('pickup-availability-drawer').forEach(enhanceDrawer);
      });
    });
  });

  if (document.body) {
    pickupObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();